
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SocialProfile } from '../types';
import EmptyState from './videos/EmptyState';
import LoadingState from './videos/LoadingState';
import ProductsGrid from './videos/ProductsGrid';
import { useFetchProducts } from './videos/useFetchProducts';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { SubscriptionLock } from '@/components/subscription/SubscriptionLock';
import { Skeleton } from '@/components/ui/skeleton';

interface VideosDisplayProps {
  selectedProfiles: string[];
  singleSelectedProfile: string;
  isSingleSelectionMode: boolean;
  profiles?: SocialProfile[];
}

// Create a new QueryClient instance
const queryClient = new QueryClient();

const VideosContent: React.FC<VideosDisplayProps> = ({
  selectedProfiles,
  profiles = [],
}) => {
  const { isSubscriptionActive, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  
  // Get profile objects from IDs
  const selectedProfileObjects = profiles.filter(profile => 
    selectedProfiles.includes(profile.id)
  );
  
  // Fetch products for the selected profiles
  const { 
    data, 
    isLoading, 
    error 
  } = useFetchProducts(selectedProfiles);

  // Display loading state when checking subscription status
  if (isSubscriptionLoading || isSubscriptionActive === null) {
    return <LoadingState />;
  }

  // If subscription is not active, show the lock screen
  if (isSubscriptionActive === false) {
    return (
      <SubscriptionLock 
        title="Contenu verrouillé"
        description="Le contenu détaillé de vos profils n'est disponible qu'avec un abonnement actif."
        buttonText="Débloquer le contenu"
      />
    );
  }

  if (error) {
    console.error("Erreur lors de la récupération des produits:", error);
    return (
      <div className="flex items-center justify-center h-full p-6 text-red-500 font-medium">
        Une erreur est survenue lors du chargement des contenus.
      </div>
    );
  }

  if (selectedProfiles.length === 0) {
    return <EmptyState noProfiles={true} />;
  }

  if (isLoading) {
    return <LoadingState />;
  }

  if (!data || data.products.length === 0) {
    return <EmptyState selectedProfileObjects={selectedProfileObjects} />;
  }

  return (
    <div className="h-full overflow-auto bg-pastel-blueLight dark:bg-[#121624]">
      <ProductsGrid 
        products={data.products} 
        profileName={data.profileName} 
        selectedProfiles={selectedProfiles}
      />
    </div>
  );
};

// Wrapper component that provides the QueryClient
const VideosDisplay: React.FC<VideosDisplayProps> = (props) => {
  return (
    <QueryClientProvider client={queryClient}>
      <VideosContent {...props} />
    </QueryClientProvider>
  );
};

export default VideosDisplay;
